import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import StudioPageShell from '../../components/studio/StudioPageShell';
import StudioV2StarterGallery from '../../components/studio/StudioV2StarterGallery';

/** Bookmarkable catalog of flow + app starters (same cards as Studio home). */
export default function StudioTemplatesPage() {
  const location = useLocation();
  const [starterError, setStarterError] = useState(null);

  useEffect(() => {
    const raw = location.hash.replace(/^#/, '').trim().toLowerCase();
    const id = raw === 'flows' || raw === 'starter-flows'
      ? 'starter-flows'
      : raw === 'apps' || raw === 'starter-apps'
        ? 'starter-apps'
        : '';
    if (!id) return;
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [location.hash, location.pathname]);

  return (
    <StudioPageShell
      title="Starter templates"
      subtitle="Shortcut from a familiar scenario to a working flow and app — edit nodes and bindings to match your reality."
      backTo="/studio"
      backLabel="Studio"
      maxWidth={960}
      actions={(
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <MuiLink component={RouterLink} to="/studio/templates#starter-flows" underline="hover" sx={{ fontSize: 13 }}>
            Flow starters
          </MuiLink>
          <Typography component="span" variant="caption" color="text.secondary">·</Typography>
          <MuiLink component={RouterLink} to="/studio/templates#starter-apps" underline="hover" sx={{ fontSize: 13 }}>
            App shells
          </MuiLink>
        </Stack>
      )}
    >
      {starterError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStarterError(null)}>
          {starterError}
        </Alert>
      )}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Vertical shells (permit, intake, support…) are layouts you bind to flows you design for that job.
            Flow starters teach patterns — branching, API calls, human input — you mix into real processes.
            Share this page (
            <code>#starter-flows</code>
            ,
            {' '}
            <code>#starter-apps</code>
            ).
            {' '}
            <Button component={RouterLink} to="/studio/help/use-cases#from-real-world-use-case-to-flow-and-app" variant="text" size="small" sx={{ textTransform: 'none', px: 0.5 }}>Use case playbook</Button>
          </Typography>
          <StudioV2StarterGallery
            mode="both"
            variant="full"
            onError={setStarterError}
            flowSectionId="starter-flows"
            appSectionId="starter-apps"
          />
          <Box>
            <Button component={RouterLink} to="/studio/flows" variant="outlined" size="small" sx={{ mr: 1, textTransform: 'none' }}>
              All flows
            </Button>
            <Button component={RouterLink} to="/studio/apps" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
              All apps
            </Button>
          </Box>
        </Stack>
      </Paper>
    </StudioPageShell>
  );
}
